import React, { useState, useEffect } from 'react';
import { X, Save, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { partiesAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiErrors';

// Tab components for each section
const TABS = [
  { id: 'basic', label: 'Basic', fields: ['name', 'phone', 'email'] },
  { id: 'business', label: 'Business', fields: ['gstin', 'pan', 'company_name'] },
  { id: 'address', label: 'Address', fields: ['billing_address', 'shipping_address'] },
  { id: 'credit', label: 'Credit & Terms', fields: ['opening_balance_amount', 'credit_limit', 'credit_period_days'] },
  { id: 'other', label: 'Other', fields: ['contact_person_name', 'bank_account_holder', 'latitude', 'longitude'] },
];

const AddPartyModal = ({ party, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    // Basic
    name: '',
    phone: '',
    email: '',
    whatsapp_same_as_mobile: true,
    type: 'customer', // customer, supplier, both
    status: 'active',
    
    // Business
    gstin: '',
    pan: '',
    company_name: '',
    category: '',
    tags: '',
    
    // Address
    billing_address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    },
    shipping_same_as_billing: true,
    shipping_address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    },
    
    // Credit & Terms
    opening_balance_amount: 0,
    opening_balance_type: 'TO_COLLECT', // TO_COLLECT, TO_PAY
    credit_period_days: 0,
    credit_limit: 0,
    
    // Other
    contact_person_name: '',
    contact_person_dob: '',
    notes: '',
    
    // Bank Details
    bank_account_holder: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_name: '',
    upi_id: '',
    
    // Geolocation
    latitude: '',
    longitude: '',
  });

  useEffect(() => {
    if (party) {
      setFormData({
        ...formData,
        ...party,
        billing_address: party.billing_address || formData.billing_address,
        shipping_address: party.shipping_address || formData.shipping_address,
      });
    }
  }, [party]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: value
      }
    }));
    // Clear address field errors
    const errorKey = `${addressType}.${field}`;
    if (fieldErrors[errorKey]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Basic tab validation
    if (!(formData.name || '').trim()) {
      errors.name = 'Party name is required (minimum 2 characters)';
    } else if ((formData.name || '').trim().length < 2) {
      errors.name = 'Party name must be at least 2 characters';
    }

    if (!(formData.phone || '').trim()) {
      errors.phone = 'Mobile number is required';
    } else if (!/^\+?\d{10,15}$/.test((formData.phone || '').replace(/\s/g, ''))) {
      errors.phone = 'Enter valid mobile number (10 digits for India or +country code)';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Enter a valid email address';
    }
    
    // Business tab validation
    if (formData.gstin && formData.gstin.length > 15) {
      errors.gstin = 'GSTIN cannot exceed 15 characters';
    }
    
    if (formData.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan)) {
      errors.pan = 'Invalid PAN format (must be 10 characters: ABCDE1234F)';
    }
    
    // Address tab validation
    if (formData.billing_address.pincode && !/^\d{6}$/.test(formData.billing_address.pincode)) {
      errors['billing_address.pincode'] = 'Pincode must be 6 digits';
    }
    
    if (formData.shipping_address.pincode && !formData.shipping_same_as_billing && !/^\d{6}$/.test(formData.shipping_address.pincode)) {
      errors['shipping_address.pincode'] = 'Pincode must be 6 digits';
    }
    
    // Credit tab validation
    if (formData.opening_balance_amount < 0) {
      errors.opening_balance_amount = 'Opening balance cannot be negative';
    }
    
    if (formData.credit_limit < 0) {
      errors.credit_limit = 'Credit limit cannot be negative';
    }
    
    if (formData.credit_period_days < 0) {
      errors.credit_period_days = 'Credit period cannot be negative';
    }
    
    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      // Find which tab has the first error
      const errorFields = Object.keys(errors);
      const firstError = errorFields[0];
      
      if (['name', 'phone', 'email'].includes(firstError)) {
        setActiveTab('basic');
      } else if (['gstin', 'pan', 'company_name'].includes(firstError)) {
        setActiveTab('business');
      } else if (firstError.includes('address')) {
        setActiveTab('address');
      } else if (['opening_balance_amount', 'credit_limit', 'credit_period_days'].includes(firstError)) {
        setActiveTab('credit');
      }
      
      // Set summary error message
      setError(`Please fix ${Object.keys(errors).length} validation error${Object.keys(errors).length > 1 ? 's' : ''} below`);
      
      // Scroll to first error field after a brief delay
      setTimeout(() => {
        const firstErrorField = document.querySelector('.border-red-500');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      return false;
    }
    
    setError('');
    return true;
  };

  const handleClose = () => {
    onClose();
  };

  const getTabHasErrors = (tabFields) => {
    return tabFields.some(field => {
      if (field.includes('address')) {
        return Object.keys(fieldErrors).some(key => key.includes(field));
      }
      return fieldErrors[field];
    });
  };

  const handleSubmit = async (saveAndNew = false) => {
    setError('');
    setFieldErrors({});
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data for API - map frontend field names to backend structure
      const apiData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        whatsapp_same_as_mobile: formData.whatsapp_same_as_mobile,
        type: formData.type,
        status: formData.status,
        
        // Business Details
        gstin: formData.gstin || null,
        pan: formData.pan || null,
        company_name: formData.company_name || null,
        category: formData.category || null,
        tags: formData.tags || null,
        
        // Addresses
        billing_address: formData.billing_address,
        shipping_address: formData.shipping_same_as_billing 
          ? formData.billing_address 
          : formData.shipping_address,
        shipping_same_as_billing: formData.shipping_same_as_billing,
        
        // Opening Balance & Credit
        opening_balance: {
          amount: parseFloat(formData.opening_balance_amount) || 0,
          direction: formData.opening_balance_type
        },
        credit_limit: parseFloat(formData.credit_limit) || 0,
        credit_days: parseInt(formData.credit_period_days) || 0,
        
        // Contact Person
        contact_person: {
          name: formData.contact_person_name || null,
          dob: formData.contact_person_dob || null,
          notes: formData.notes || null
        },
        
        // Bank Details
        bank_details: {
          holder_name: formData.bank_account_holder || null,
          account_no: formData.bank_account_number || null,
          ifsc: formData.bank_ifsc || null,
          bank_name: formData.bank_name || null,
          upi_id: formData.upi_id || null
        },
        
        // Geolocation
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      };

      if (party) {
        await partiesAPI.update(party.id, apiData);
        toast.success('Party updated successfully');
      } else {
        await partiesAPI.create(apiData);
        toast.success('Party created successfully');
      }

      onSave(); // This will trigger refresh in parent
      
      if (saveAndNew && !party) {
        // Reset form but keep party type and status
        setFormData({
          ...formData,
          name: '',
          phone: '',
          email: '',
          whatsapp_same_as_mobile: true,
          gstin: '',
          pan: '',
          company_name: '',
          category: '',
          tags: '',
          billing_address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            pincode: '',
            country: 'India',
          },
          shipping_address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            pincode: '',
            country: 'India',
          },
          opening_balance_amount: 0,
          credit_period_days: 0,
          credit_limit: 0,
          contact_person_name: '',
          contact_person_dob: '',
          notes: '',
          bank_account_holder: '',
          bank_account_number: '',
          bank_ifsc: '',
          bank_name: '',
          upi_id: '',
          latitude: '',
          longitude: '',
        });
        setActiveTab('basic');
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Party save error:', err);
      
      // Handle backend validation errors
      if (err.response?.status === 400 && err.response?.data?.field_errors) {
        setFieldErrors(err.response.data.field_errors);
        setError('Please fix the highlighted fields');
        toast.error('Validation failed');
      } 
      // Handle duplicate errors
      else if (err.response?.status === 409) {
        const duplicateMsg = err.response?.data?.detail || 'A party with this information already exists';
        setError(duplicateMsg);
        toast.error(duplicateMsg);
      }
      // Handle server errors
      else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
        toast.error('Server error. Please try again.');
      }
      // Generic error
      else {
        const errorMsg = getApiErrorMessage(err, 'Failed to save party');
        setError(errorMsg);
        toast.error(errorMsg);
      }
      
      // Scroll to error banner
      setTimeout(() => {
        const errorBanner = document.querySelector('.bg-red-50');
        if (errorBanner) {
          errorBanner.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      {/* Modal Container - Fixed Size (Full screen on mobile, fixed on desktop) */}
      <div className="add-party-modal-container bg-white shadow-xl flex flex-col overflow-hidden w-full h-full sm:rounded-lg sm:max-w-[920px] sm:w-[96vw] sm:h-[82vh] sm:max-h-[760px]">
        {/* Header - Sticky */}
        <div className="flex items-center justify-between p-6 border-b bg-white sticky top-0 z-10 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold">{party ? 'Edit Party' : 'Add Party'}</h2>
            <p className="text-sm text-gray-600 mt-1">Complete contact information</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs - Sticky */}
        <div className="border-b bg-white sticky top-[88px] z-10 flex-shrink-0">
          <div className="flex overflow-x-auto">
            {TABS.map(tab => {
              const hasErrors = getTabHasErrors(tab.fields);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors relative ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                  {hasErrors && (
                    <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrollable Body - Fixed Height */}
        <div className="flex-1 overflow-y-auto">
          {/* Error Banner */}
          {error && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold text-sm">Couldn't save party</p>
                  <p className="text-sm mt-1">{error}</p>
                  {Object.keys(fieldErrors).length > 0 && (
                    <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                      {Object.entries(fieldErrors).map(([field, message]) => (
                        <li key={field}>{message}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'basic' && <BasicTab formData={formData} onChange={handleInputChange} fieldErrors={fieldErrors} />}
            {activeTab === 'business' && <BusinessTab formData={formData} onChange={handleInputChange} fieldErrors={fieldErrors} />}
            {activeTab === 'address' && <AddressTab formData={formData} onChange={handleInputChange} onAddressChange={handleAddressChange} fieldErrors={fieldErrors} />}
            {activeTab === 'credit' && <CreditTab formData={formData} onChange={handleInputChange} fieldErrors={fieldErrors} />}
            {activeTab === 'other' && <OtherTab formData={formData} onChange={handleInputChange} fieldErrors={fieldErrors} />}
          </div>
        </div>

        {/* Footer - Sticky Bottom */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <div className="flex gap-3">
            {!party && (
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSubmit(true)}
                disabled={loading}
              >
                <Plus className="w-4 h-4 mr-2" />
                Save & New
              </Button>
            )}
            <Button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={loading}
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for input with error
const InputWithError = ({ id, label, value, onChange, error, required, ...props }) => (
  <div>
    <Label htmlFor={id}>{label} {required && '*'}</Label>
    <Input
      id={id}
      value={value}
      onChange={onChange}
      className={error ? 'border-red-500 focus:ring-red-500' : ''}
      {...props}
    />
    {error && (
      <p className="mt-1 text-sm text-red-600">{error}</p>
    )}
  </div>
);

// Basic Tab Component
const BasicTab = ({ formData, onChange, fieldErrors = {} }) => (
  <div className="space-y-6">
    {/* Party Type Pills */}
    <div>
      <Label>Party Type *</Label>
      <div className="flex gap-2 mt-2">
        {['customer', 'supplier', 'both'].map(type => (
          <button
            key={type}
            type="button"
            onClick={() => onChange('type', type)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              formData.type === type
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>
    </div>

    {/* Party Name */}
    <InputWithError
      id="name"
      label="Party Name"
      value={formData.name}
      onChange={(e) => onChange('name', e.target.value)}
      placeholder="Enter party name"
      error={fieldErrors.name}
      required
    />

    {/* Mobile & Email */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <InputWithError
        id="phone"
        label="Mobile Number"
        type="tel"
        value={formData.phone}
        onChange={(e) => onChange('phone', e.target.value)}
        placeholder="10-digit mobile number"
        error={fieldErrors.phone}
        required
      />
      <InputWithError
        id="email"
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => onChange('email', e.target.value)}
        placeholder="email@example.com"
        error={fieldErrors.email}
      />
    </div>

    {/* WhatsApp Checkbox */}
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id="whatsapp_same"
        checked={formData.whatsapp_same_as_mobile}
        onChange={(e) => onChange('whatsapp_same_as_mobile', e.target.checked)}
        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
      <Label htmlFor="whatsapp_same" className="cursor-pointer">
        WhatsApp same as mobile
      </Label>
    </div>

    {/* Status */}
    <div>
      <Label htmlFor="status">Status</Label>
      <Select value={formData.status} onValueChange={(value) => onChange('status', value)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// Business Tab Component
const BusinessTab = ({ formData, onChange, fieldErrors = {} }) => (
  <div className="space-y-6">
    <div>
      <InputWithError
        id="gstin"
        label="GSTIN"
        value={formData.gstin}
        onChange={(e) => onChange('gstin', e.target.value.toUpperCase())}
        placeholder="22AAAAA0000A1Z5"
        error={fieldErrors.gstin}
      />
      <p className="text-xs text-gray-500 mt-1">15-character GST identification number</p>
    </div>

    <div>
      <InputWithError
        id="pan"
        label="PAN"
        value={formData.pan}
        onChange={(e) => onChange('pan', e.target.value.toUpperCase())}
        placeholder="ABCDE1234F"
        maxLength={10}
        error={fieldErrors.pan}
      />
    </div>

    <div>
      <Label htmlFor="company_name">Company/Shop Name</Label>
      <Input
        id="company_name"
        value={formData.company_name}
        onChange={(e) => onChange('company_name', e.target.value)}
        placeholder="Business name"
      />
    </div>

    <div>
      <Label htmlFor="category">Category/Group</Label>
      <Input
        id="category"
        value={formData.category}
        onChange={(e) => onChange('category', e.target.value)}
        placeholder="e.g., Wholesale, Retail"
      />
    </div>

    <div>
      <Label htmlFor="tags">Tags</Label>
      <Input
        id="tags"
        value={formData.tags}
        onChange={(e) => onChange('tags', e.target.value)}
        placeholder="Comma-separated tags"
      />
    </div>
  </div>
);

// Address Tab Component  
const AddressTab = ({ formData, onChange, onAddressChange, fieldErrors = {} }) => (
  <div className="space-y-6">
    {/* Billing Address */}
    <div>
      <h3 className="font-semibold mb-4">Billing Address</h3>
      <div className="space-y-4">
        <div>
          <Label>Address Line 1</Label>
          <Input
            value={formData.billing_address.line1}
            onChange={(e) => onAddressChange('billing_address', 'line1', e.target.value)}
            placeholder="Street address"
          />
        </div>
        <div>
          <Label>Address Line 2</Label>
          <Input
            value={formData.billing_address.line2}
            onChange={(e) => onAddressChange('billing_address', 'line2', e.target.value)}
            placeholder="Apartment, suite, etc."
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>City</Label>
            <Input
              value={formData.billing_address.city}
              onChange={(e) => onAddressChange('billing_address', 'city', e.target.value)}
            />
          </div>
          <div>
            <Label>State</Label>
            <Input
              value={formData.billing_address.state}
              onChange={(e) => onAddressChange('billing_address', 'state', e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Pincode</Label>
            <Input
              value={formData.billing_address.pincode}
              onChange={(e) => onAddressChange('billing_address', 'pincode', e.target.value)}
              maxLength={6}
              className={fieldErrors['billing_address.pincode'] ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors['billing_address.pincode'] && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors['billing_address.pincode']}</p>
            )}
          </div>
          <div>
            <Label>Country</Label>
            <Input
              value={formData.billing_address.country}
              onChange={(e) => onAddressChange('billing_address', 'country', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>

    {/* Shipping Same as Billing */}
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id="shipping_same"
        checked={formData.shipping_same_as_billing}
        onChange={(e) => onChange('shipping_same_as_billing', e.target.checked)}
        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
      <Label htmlFor="shipping_same" className="cursor-pointer">
        Shipping address same as billing
      </Label>
    </div>

    {/* Shipping Address (if different) */}
    {!formData.shipping_same_as_billing && (
      <div>
        <h3 className="font-semibold mb-4">Shipping Address</h3>
        <div className="space-y-4">
          <div>
            <Label>Address Line 1</Label>
            <Input
              value={formData.shipping_address.line1}
              onChange={(e) => onAddressChange('shipping_address', 'line1', e.target.value)}
            />
          </div>
          <div>
            <Label>Address Line 2</Label>
            <Input
              value={formData.shipping_address.line2}
              onChange={(e) => onAddressChange('shipping_address', 'line2', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>City</Label>
              <Input
                value={formData.shipping_address.city}
                onChange={(e) => onAddressChange('shipping_address', 'city', e.target.value)}
              />
            </div>
            <div>
              <Label>State</Label>
              <Input
                value={formData.shipping_address.state}
                onChange={(e) => onAddressChange('shipping_address', 'state', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Pincode</Label>
              <Input
                value={formData.shipping_address.pincode}
                onChange={(e) => onAddressChange('shipping_address', 'pincode', e.target.value)}
                maxLength={6}
                className={fieldErrors['shipping_address.pincode'] ? 'border-red-500 focus:ring-red-500' : ''}
              />
              {fieldErrors['shipping_address.pincode'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['shipping_address.pincode']}</p>
              )}
            </div>
            <div>
              <Label>Country</Label>
              <Input
                value={formData.shipping_address.country}
                onChange={(e) => onAddressChange('shipping_address', 'country', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Credit & Terms Tab
const CreditTab = ({ formData, onChange, fieldErrors = {} }) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
      <p className="text-sm text-blue-800">
        <strong>To Collect:</strong> Customer owes you money<br />
        <strong>To Pay:</strong> You owe money to supplier
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="opening_balance_amount">Opening Balance (₹)</Label>
        <Input
          id="opening_balance_amount"
          type="number"
          value={formData.opening_balance_amount}
          onChange={(e) => onChange('opening_balance_amount', e.target.value)}
          min="0"
          step="0.01"
          className={fieldErrors.opening_balance_amount ? 'border-red-500 focus:ring-red-500' : ''}
        />
        {fieldErrors.opening_balance_amount && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors.opening_balance_amount}</p>
        )}
      </div>
      <div>
        <Label>Opening Balance Type</Label>
        <Select 
          value={formData.opening_balance_type} 
          onValueChange={(value) => onChange('opening_balance_type', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="TO_COLLECT">To Collect (Receivable)</SelectItem>
            <SelectItem value="TO_PAY">To Pay (Payable)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="credit_period_days">Credit Period (days)</Label>
        <Input
          id="credit_period_days"
          type="number"
          value={formData.credit_period_days}
          onChange={(e) => onChange('credit_period_days', e.target.value)}
          min="0"
          className={fieldErrors.credit_period_days ? 'border-red-500 focus:ring-red-500' : ''}
        />
        {fieldErrors.credit_period_days && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors.credit_period_days}</p>
        )}
      </div>
      <div>
        <Label htmlFor="credit_limit">Credit Limit (₹)</Label>
        <Input
          id="credit_limit"
          type="number"
          value={formData.credit_limit}
          onChange={(e) => onChange('credit_limit', e.target.value)}
          min="0"
          step="0.01"
          className={fieldErrors.credit_limit ? 'border-red-500 focus:ring-red-500' : ''}
        />
        {fieldErrors.credit_limit && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors.credit_limit}</p>
        )}
      </div>
    </div>
  </div>
);

// Other Tab (Contact, Bank, Geo)
const OtherTab = ({ formData, onChange, fieldErrors = {} }) => (
  <div className="space-y-6">
    {/* Contact Person */}
    <div>
      <h3 className="font-semibold mb-4">Contact Person</h3>
      <div className="space-y-4">
        <div>
          <Label htmlFor="contact_person_name">Contact Person Name</Label>
          <Input
            id="contact_person_name"
            value={formData.contact_person_name}
            onChange={(e) => onChange('contact_person_name', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="contact_person_dob">Date of Birth</Label>
          <Input
            id="contact_person_dob"
            type="date"
            value={formData.contact_person_dob}
            onChange={(e) => onChange('contact_person_dob', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="notes">Notes/Remarks</Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => onChange('notes', e.target.value)}
            rows={3}
          />
        </div>
      </div>
    </div>

    {/* Bank Details */}
    <div>
      <h3 className="font-semibold mb-4">Bank Details (Optional)</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="bank_account_holder">Account Holder Name</Label>
            <Input
              id="bank_account_holder"
              value={formData.bank_account_holder}
              onChange={(e) => onChange('bank_account_holder', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="bank_account_number">Account Number</Label>
            <Input
              id="bank_account_number"
              value={formData.bank_account_number}
              onChange={(e) => onChange('bank_account_number', e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="bank_ifsc">IFSC Code</Label>
            <Input
              id="bank_ifsc"
              value={formData.bank_ifsc}
              onChange={(e) => onChange('bank_ifsc', e.target.value.toUpperCase())}
              maxLength={11}
            />
          </div>
          <div>
            <Label htmlFor="bank_name">Bank Name</Label>
            <Input
              id="bank_name"
              value={formData.bank_name}
              onChange={(e) => onChange('bank_name', e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="upi_id">UPI ID</Label>
          <Input
            id="upi_id"
            value={formData.upi_id}
            onChange={(e) => onChange('upi_id', e.target.value)}
            placeholder="username@upi"
          />
        </div>
      </div>
    </div>

    {/* Geolocation */}
    <div>
      <h3 className="font-semibold mb-4">Geolocation (For Field Tracking)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="latitude">Latitude</Label>
          <Input
            id="latitude"
            type="number"
            step="any"
            value={formData.latitude}
            onChange={(e) => onChange('latitude', e.target.value)}
            placeholder="e.g., 28.7041"
          />
        </div>
        <div>
          <Label htmlFor="longitude">Longitude</Label>
          <Input
            id="longitude"
            type="number"
            step="any"
            value={formData.longitude}
            onChange={(e) => onChange('longitude', e.target.value)}
            placeholder="e.g., 77.1025"
          />
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Enter coordinates manually or use map-based location picker
      </p>
    </div>
  </div>
);

export default AddPartyModal;
